import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { initialState } from './seed'
import { loadState, saveState } from './database'
import { loadCloudState, mergeCloudState, saveCloudState, toCloudState } from './cloud'
import type { AppState, AthleteProfile, BodyMeasurement, CoachMessage, CoachProposal, Exercise, Preferences, WorkoutSession } from '../domain/types'

interface StrataContextValue {
  state: AppState
  ready: boolean
  syncStatus: 'syncing' | 'synced' | 'offline'
  upsertSession: (session: WorkoutSession) => void
  addMeasurement: (measurement: BodyMeasurement) => void
  updateExercise: (exercise: Exercise) => void
  updatePreferences: (preferences: Preferences) => void
  saveAthleteProfile: (profile: AthleteProfile, currentWeight: number) => void
  toggleDietDay: (date: string) => void
  addCoachMessage: (message: CoachMessage) => void
  applyCoachProposal: (proposal: CoachProposal) => void
}

const StrataContext = createContext<StrataContextValue | null>(null)

export function StrataProvider({ accountId, children }: { accountId: string; children: ReactNode }) {
  const [state, setState] = useState<AppState>(initialState)
  const [ready, setReady] = useState(false)
  const [syncStatus, setSyncStatus] = useState<'syncing' | 'synced' | 'offline'>('syncing')
  const saveGeneration = useRef(0)

  useEffect(() => {
    let active = true
    const initialize = async () => {
      const saved = await loadState(accountId).catch(() => undefined)
      let local = saved ? normalizeState(saved) : initialState
      if (saved && !local.updatedAt && local.profile.onboardingComplete) local = { ...local, updatedAt: new Date().toISOString() }
      try {
        const cloud = await loadCloudState()
        if (cloud && (!local.updatedAt || cloud.updatedAt >= local.updatedAt)) local = mergeCloudState(local, cloud)
        if (active) setSyncStatus('synced')
      } catch {
        if (active) setSyncStatus('offline')
      }
      if (active) {
        setState(local)
        setReady(true)
      }
    }
    void initialize()
    return () => { active = false }
  }, [accountId])

  useEffect(() => {
    if (!ready) return
    const generation = ++saveGeneration.current
    setSyncStatus('syncing')
    const timeout = window.setTimeout(() => {
      const saves: Promise<void>[] = [saveState(accountId, state)]
      if (state.updatedAt) saves.push(saveCloudState(toCloudState(state)))
      Promise.all(saves)
        .then(() => { if (generation === saveGeneration.current) setSyncStatus('synced') })
        .catch(() => { if (generation === saveGeneration.current) setSyncStatus('offline') })
    }, 450)
    return () => window.clearTimeout(timeout)
  }, [accountId, ready, state])

  const value = useMemo<StrataContextValue>(() => ({
    state,
    ready,
    syncStatus,
    upsertSession: (session) => setState((current) => {
      const exists = current.sessions.some((item) => item.id === session.id)
      const next = touch({
        ...current,
        sessions: exists ? current.sessions.map((item) => item.id === session.id ? session : item) : [...current.sessions, session],
      })
      void saveState(accountId, next)
      void saveCloudState(toCloudState(next)).catch(() => undefined)
      return next
    }),
    addMeasurement: (measurement) => setState((current) => touch({ ...current, measurements: [...current.measurements, measurement] })),
    updateExercise: (exercise) => setState((current) => touch({
      ...current,
      exercises: current.exercises.map((item) => item.id === exercise.id ? exercise : item),
    })),
    updatePreferences: (preferences) => setState((current) => touch({ ...current, preferences })),
    saveAthleteProfile: (profile, currentWeight) => setState((current) => {
      const latest = [...current.measurements].sort((a, b) => b.date.localeCompare(a.date))[0]
      const measurements = currentWeight > 0 && (!latest || Math.abs(latest.weight - currentWeight) >= .01)
        ? [...current.measurements, { id: crypto.randomUUID(), date: new Date().toISOString(), weight: currentWeight }]
        : current.measurements
      return touch({ ...current, profile, measurements })
    }),
    toggleDietDay: (date) => setState((current) => {
      const exists = current.dietCheckIns.some((item) => item.date === date)
      return touch({
        ...current,
        dietCheckIns: exists
          ? current.dietCheckIns.filter((item) => item.date !== date)
          : [...current.dietCheckIns, { date, completedAt: new Date().toISOString() }],
      })
    }),
    addCoachMessage: (message) => setState((current) => touch({
      ...current,
      coachMessages: [...current.coachMessages, message].slice(-80),
    })),
    applyCoachProposal: (proposal) => setState((current) => touch({
      ...current,
      exercises: current.exercises.map((exercise) => {
        const adjustment = proposal.exerciseAdjustments?.find((item) => item.exerciseId === exercise.id)
        if (!adjustment) return exercise
        return {
          ...exercise,
          preferredWeight: finiteBetween(adjustment.preferredWeightKg, 0, 1000) ?? exercise.preferredWeight,
          increment: finiteBetween(adjustment.incrementKg, .1, 100) ?? exercise.increment,
          restSeconds: finiteBetween(adjustment.restSeconds, 15, 900) ?? exercise.restSeconds,
        }
      }),
      templates: current.templates.map((template) => ({
        ...template,
        entries: template.entries.map((entry) => {
          const adjustment = proposal.routineAdjustments?.find((item) => item.templateId === template.id && item.exerciseId === entry.exerciseId)
          if (!adjustment) return entry
          const minReps = finiteBetween(adjustment.minReps, 1, 200) ?? entry.minReps
          const maxReps = Math.max(minReps, finiteBetween(adjustment.maxReps, 1, 200) ?? entry.maxReps)
          return {
            ...entry,
            sets: finiteBetween(adjustment.sets, 1, 12) ?? entry.sets,
            minReps,
            maxReps,
            restSeconds: finiteBetween(adjustment.restSeconds, 15, 900) ?? entry.restSeconds,
            targetRir: typeof adjustment.targetRir === 'string' && adjustment.targetRir.length <= 12 ? adjustment.targetRir : entry.targetRir,
          }
        }),
      })),
    })),
  }), [state, ready, syncStatus])

  return <StrataContext.Provider value={value}>{children}</StrataContext.Provider>
}

function touch(state: AppState): AppState {
  return { ...state, updatedAt: new Date().toISOString() }
}

function normalizeState(saved: AppState): AppState {
  return {
    ...initialState,
    ...saved,
    exercises: initialState.exercises.map((exercise) => {
      const customized = saved.exercises?.find((item) => item.id === exercise.id)
      return {
        ...exercise,
        increment: customized?.increment ?? exercise.increment,
        preferredWeight: customized?.preferredWeight,
        restSeconds: customized?.restSeconds ?? exercise.restSeconds,
        videoUrl: customized?.videoUrl ?? exercise.videoUrl,
        media: customized?.media,
        mediaType: customized?.mediaType,
      }
    }),
    templates: saved.templates?.length ? saved.templates : initialState.templates,
    sessions: saved.sessions ?? [],
    measurements: saved.measurements ?? [],
    preferences: { ...initialState.preferences, ...saved.preferences },
    profile: {
      ...initialState.profile,
      ...saved.profile,
      nutritionTargets: {
        ...initialState.profile.nutritionTargets,
        ...saved.profile?.nutritionTargets,
      },
    },
    dietCheckIns: saved.dietCheckIns ?? [],
    coachMessages: saved.coachMessages ?? [],
  }
}

function finiteBetween(value: number | undefined, minimum: number, maximum: number) {
  if (value === undefined || !Number.isFinite(value)) return undefined
  return Math.min(maximum, Math.max(minimum, value))
}

export function useStrata(): StrataContextValue {
  const value = useContext(StrataContext)
  if (!value) throw new Error('useStrata debe utilizarse dentro de StrataProvider')
  return value
}
